import type { SiteContent } from '@/content/types';

/**
 * Tout le texte du site, en français. Les composants n'écrivent aucune phrase
 * en dur : traduire le site revient à fournir un autre objet `SiteContent`.
 */
export const siteContent: SiteContent = {
  navigation: {
    homeLabel: 'Oxelte, retour à l’accueil',
    links: [
      { label: 'Studio', href: '#studio' },
      { label: 'Expertises', href: '#expertises' },
      { label: 'Applications', href: '#applications' },
      { label: 'Méthode', href: '#methode' },
    ],
    cta: { label: 'Démarrer un projet', href: '#brief' },
    openMenu: 'Ouvrir le menu',
    closeMenu: 'Fermer le menu',
  },

  journey: {
    chapters: {
      origin: {
        index: '01',
        title: 'Origine',
        star: 'Prima',
        coordinates: 'AD 05h 34m · DÉC +22° 00′',
        href: '#top',
      },
      studio: {
        index: '02',
        title: 'Le studio',
        star: 'Halo',
        coordinates: 'AD 18h 36m · DÉC +38° 47′',
        href: '#studio',
      },
      expertises: {
        index: '03',
        title: 'Savoir-faire',
        star: 'Indigo',
        coordinates: 'AD 12h 30m · DÉC +12° 23′',
        href: '#expertises',
      },
      applications: {
        index: '04',
        title: 'Applications',
        star: 'Orbite',
        coordinates: 'AD 20h 41m · DÉC +45° 16′',
        href: '#applications',
      },
      method: {
        index: '05',
        title: 'Méthode',
        star: 'Cap',
        coordinates: 'AD 06h 45m · DÉC +16° 42′',
        href: '#methode',
      },
      brief: {
        index: '06',
        title: 'Votre projet',
        star: 'Lancement',
        coordinates: 'AD 14h 15m · DÉC +19° 10′',
        href: '#brief',
      },
      horizon: {
        index: '07',
        title: 'Horizon',
        star: 'Œil',
        coordinates: 'AD 00h 42m · DÉC +41° 16′',
        href: '#horizon',
      },
    },
    starLabel: 'Trou noir',
    crossing: 'Traversée',
    railLabel: 'Chapitres du voyage',
  },

  hero: {
    title: 'Des expériences digitales',
    titleAccent: 'taillées pour demain.',
    lead: 'Sites et applications mobiles d’exception, pour les maisons qui ne transigent pas sur la qualité.',
    cta: { label: 'Démarrer un projet', href: '#brief' },
    journeyCta: 'Franchir l’horizon',
    beats: [
      { word: 'Idées', line: 'Tout commence par votre histoire.' },
      { word: 'Technologie', line: 'Nous la traduisons en code, en 3D, en mouvement.' },
      { word: 'Impact', line: 'Pour que chaque visite laisse une trace.' },
    ],
    posterAlt: 'Un trou noir entouré d’un disque de lumière violette, sur fond d’étoiles.',
  },

  manifesto: {
    title: 'Un studio,',
    titleAccent: 'une exigence.',
    lead: 'Oxelte conçoit et développe des sites et des applications mobiles pour les artisans d’exception et les grandes maisons. La même exigence, quelle que soit la taille du projet.',
    pillars: [
      { title: 'Dessiné', text: 'Chaque détail, du premier croquis au dernier pixel.' },
      { title: 'Pensé', text: 'Chaque animation sert le récit de votre marque.' },
      { title: 'Mesuré', text: 'Chaque milliseconde compte, sur tous les écrans.' },
    ],
    signature: ['Idées', 'Technologie', 'Impact'],
  },

  expertises: {
    title: 'Quatre savoir-faire, un seul studio.',
    lead: 'Du premier croquis à la mise en ligne, un seul interlocuteur.',
    items: [
      {
        index: '01',
        title: 'Sites d’exception',
        description:
          'Sites vitrines et boutiques en ligne dessinés et développés sur mesure, rapides et bien référencés.',
        skills: ['Direction artistique', 'E-commerce', 'Référencement'],
        visual: 'architecture',
        visualAlt: 'Architecture monumentale éclairée de violet, de nuit.',
      },
      {
        index: '02',
        title: 'Applications mobiles',
        description:
          'Applications iOS et Android pensées comme des objets de marque : fluides, sobres, mémorables.',
        skills: ['iOS et Android', 'Parcours d’achat', 'Fidélité'],
        visual: 'app',
        visualAlt: 'Flacon de parfum en verre fumé, halo violet.',
      },
      {
        index: '03',
        title: '3D et motion',
        description:
          'Scènes temps réel, animations au défilement, expériences immersives comme celle que vous venez de traverser.',
        skills: ['WebGL', 'Animation', 'Expériences immersives'],
        visual: 'monogram',
        visualAlt: 'Le monogramme OX en métal argent et violet.',
      },
      {
        index: '04',
        title: 'Identité et design',
        description:
          'Identité visuelle, systèmes de design et interfaces qui restent cohérents sur chaque écran.',
        skills: ['Identité visuelle', 'Design system', 'UI'],
        visual: 'craft',
        visualAlt: 'Mains d’artisan taillant la pierre dans un rai de lumière violette.',
      },
    ],
  },

  mobile: {
    title: 'Des applications aussi soignées',
    titleAccent: 'que vos produits.',
    lead: 'Nous concevons des applications iOS et Android avec la même exigence visuelle que vos sites, et la fluidité qu’on attend d’un objet de luxe.',
    features: [
      {
        title: 'Natif sur iOS et Android',
        description: 'Une expérience à la hauteur de chaque plateforme.',
      },
      {
        title: 'Achat et réservation',
        description: 'Des parcours courts, rassurants, sans friction.',
      },
      { title: 'Lien durable', description: 'Notifications, fidélité, contenus exclusifs.' },
    ],
    apps: {
      watch: {
        label:
          'Application d’une maison horlogère : fiche de la montre Tourbillon Obsidienne, choix de la matière, réservation d’un essai en boutique.',
        brand: 'Aurèle',
        collection: 'Collection Nocturne',
        name: 'Tourbillon Obsidienne',
        price: '48 500 €',
        gallery: '360°',
        materials: ['Acier poli', 'Or rose', 'Titane'],
        specs: [
          { value: '41 mm', label: 'Boîtier' },
          { value: '72 h', label: 'Réserve' },
          { value: '50 m', label: 'Étanche' },
        ],
        booking: {
          title: 'Essai en boutique',
          days: [
            { weekday: 'Mar', date: '14' },
            { weekday: 'Mer', date: '15' },
            { weekday: 'Jeu', date: '16' },
            { weekday: 'Ven', date: '17' },
          ],
          chosenDay: 2,
          slots: ['11 h', '15 h 30', '18 h'],
          chosenSlot: 1,
        },
        action: 'Réserver un essai',
        toast: { title: 'Essai confirmé', detail: 'Jeudi 16, 15 h 30, boutique Paris 8e' },
      },
      perfume: {
        label:
          'Application d’une parfumerie : le parfum Éclat Violet, sa pyramide olfactive, le choix du flacon et le paiement express.',
        brand: 'Maison Iris',
        kind: 'Eau de parfum',
        name: 'Éclat Violet',
        notes: [
          { stage: 'Tête', value: 'Bergamote, poivre rose' },
          { stage: 'Cœur', value: 'Iris, violette' },
          { stage: 'Fond', value: 'Ambre, santal' },
        ],
        sizes: [
          { label: '50 ml', price: '145 €' },
          { label: '100 ml', price: '210 €' },
        ],
        action: 'Ajouter au panier',
        express: 'Paiement express',
        reassurance: 'Livraison offerte, échantillons inclus',
        tabs: ['Accueil', 'Explorer', 'Panier', 'Profil'],
      },
    },
    caption: 'Concepts d’interface réalisés par le studio.',
  },

  method: {
    title: 'Une méthode, quatre temps.',
    lead: 'Vous validez chaque étape avant la suivante. Aucune surprise.',
    steps: [
      {
        title: 'Écouter',
        description: 'Un atelier pour comprendre votre métier, vos clients et vos ambitions.',
      },
      {
        title: 'Dessiner',
        description: 'Direction artistique, maquettes et prototype animé, validés ensemble.',
      },
      {
        title: 'Développer',
        description: 'Code sur mesure, 3D temps réel, performances mesurées à chaque livraison.',
      },
      {
        title: 'Lancer',
        description: 'Mise en ligne, référencement et suivi. Nous restons à vos côtés après.',
      },
    ],
  },

  brief: {
    title: 'Parlons de votre',
    titleAccent: 'projet.',
    lead: 'Trois étapes, deux minutes. Vous recevez une première approche personnalisée.',
    promises: [
      'Une réponse écrite par nous, pas un devis automatique',
      'Un appel de 30 minutes pour cadrer ensemble',
      'Vos informations servent uniquement à vous répondre',
    ],
    directContact: { label: 'Plutôt un échange direct ?', email: '[adresse e-mail]' },
    duration: 'Environ 2 min',
    stepLabel: (current, total) => `Étape ${current} sur ${total}`,
    steps: {
      project: { title: 'Votre projet', question: 'Quel type de projet ?' },
      scope: { title: 'Budget et délai', question: 'Quel budget envisagez-vous ?' },
      contact: { title: 'Vos coordonnées', question: 'Comment vous joindre ?' },
    },
    projectTypes: {
      showcase: {
        label: 'Site vitrine',
        description: 'Présenter votre maison et votre savoir-faire.',
      },
      ecommerce: {
        label: 'Boutique en ligne',
        description: 'Vendre en ligne avec une expérience premium.',
      },
      'mobile-app': {
        label: 'Application mobile',
        description: 'iOS et Android, pensée comme un objet de marque.',
      },
      immersive: {
        label: 'Expérience 3D',
        description: 'Lancement produit, scène immersive, événement.',
      },
      redesign: { label: 'Refonte', description: 'Moderniser un site existant sans tout perdre.' },
    },
    brandQuestion: 'Avez-vous déjà une identité visuelle ?',
    brandStatuses: {
      existing: { label: 'Oui' },
      'in-progress': { label: 'En cours' },
      none: { label: 'Non, à créer' },
    },
    budgetQuestion: 'Quel budget envisagez-vous ?',
    budgets: {
      'range-1': { label: 'Moins de [X] k€' },
      'range-2': { label: '[X] à [Y] k€' },
      'range-3': { label: 'Plus de [Y] k€' },
      undecided: { label: 'Je ne sais pas encore' },
    },
    timelineQuestion: 'Pour quand ?',
    timelines: {
      'under-1-month': { label: 'Moins d’un mois' },
      '1-3-months': { label: '1 à 3 mois' },
      '3-6-months': { label: '3 à 6 mois' },
      flexible: { label: 'Pas de date fixe' },
    },
    optional: 'facultatif',
    fields: {
      fullName: { label: 'Nom complet', placeholder: 'Camille Arnaud' },
      email: {
        label: 'E-mail professionnel',
        placeholder: 'camille@maison.fr',
        hint: 'Uniquement pour vous répondre.',
      },
      company: { label: 'Entreprise', placeholder: 'Maison Arnaud' },
      phone: { label: 'Téléphone', placeholder: '06 12 34 56 78' },
      message: {
        label: 'Votre projet en quelques mots',
        placeholder:
          'Une application pour notre nouvelle collection, avec réservation en boutique.',
      },
    },
    errors: {
      required: 'Ce champ est nécessaire pour vous répondre.',
      'email-invalid': 'Cette adresse semble incomplète, par exemple nom@domaine.fr',
      'too-long': 'Ce texte est un peu long, raccourcissez-le.',
      projectType: 'Choisissez un type de projet pour continuer.',
      scope: 'Indiquez un budget et un délai, même approximatifs.',
      unavailable:
        'L’envoi n’a pas abouti. Vos réponses sont conservées, réessayez dans un instant.',
    },
    actions: {
      next: 'Continuer',
      back: 'Retour',
      edit: 'Modifier',
      submit: 'Envoyer mon brief',
      submitting: 'Envoi en cours',
      retry: 'Réessayer',
      restart: 'Envoyer un autre brief',
    },
    consent:
      'En envoyant ce formulaire, vous acceptez que nous utilisions ces informations pour vous recontacter. Rien d’autre.',
    success: {
      title: (firstName) => `Merci ${firstName}, c’est bien reçu.`,
      body: (email) =>
        `Nous étudions votre brief et revenons vers vous très vite à l’adresse ${email}.`,
      reference: (reference) => `Référence ${reference}`,
    },
  },

  footer: {
    tagline: 'Studio digital',
    signature: ['Idées', 'Technologie', 'Impact'],
    cta: { label: 'Démarrer un projet', href: '#brief' },
    legal: [
      { label: 'Mentions légales', href: '#mentions-legales' },
      { label: 'Confidentialité', href: '#confidentialite' },
      { label: 'LinkedIn', href: '#linkedin' },
      { label: 'Instagram', href: '#instagram' },
    ],
    copyright: '© 2026 Oxelte',
  },
};
